import { defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import classNames from 'classnames';
import warning from 'warning';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import LoaderDots from '../LoaderDots/index.js';
import { InputRowContext } from '../InputRow/index.js';
import getAnchorProps from './get-anchor-props.js';
import getButtonProps from './get-button-props.js';
import styles from './themed.module.scss';
var loaderDotsTheme = {
  primary: 'inverse',
  secondary: 'brand',
  tertiary: 'muted'
};

var withIcon = function withIcon(children, _ref) {
  var _classNames;

  var icon = _ref.icon;

  if (!icon) {
    return children;
  }

  return React.createElement("span", {
    className: styles.flexCenter
  }, React.createElement("span", {
    className: classNames((_classNames = {}, _defineProperty(_classNames, styles.iconContainer, true), _defineProperty(_classNames, styles.iconContainerHasChildren, children), _classNames))
  }, icon), children);
};

var withLoader = function withLoader(children, _ref2) {
  var isLoading = _ref2.isLoading,
      theme = _ref2.theme;

  if (!isLoading) {
    return children;
  }

  return React.createElement("span", {
    className: styles.loaderContainer
  }, React.createElement("span", {
    className: styles.absoluteCenter
  }, React.createElement(LoaderDots, {
    theme: loaderDotsTheme[theme],
    size: "small"
  })), React.createElement("span", {
    className: styles.hidden
  }, children));
};

var withFlexWrapper = function withFlexWrapper(children, _ref3) {
  var _classNames2;

  var size = _ref3.size;
  return React.createElement("span", {
    className: classNames((_classNames2 = {}, _defineProperty(_classNames2, styles.flexWrapper, true), _defineProperty(_classNames2, styles.flexWrapperSizeSmall, size === 'small'), _defineProperty(_classNames2, styles.flexWrapperSizeLarge, size === 'large'), _classNames2))
  }, children);
};

var Themed = React.forwardRef(function (props, ref) {
  warning(props.children || props.accessibilityLabel || props.icon && props.children, 'The prop `accessibilityLabel` must be provided to the button or link when `icon` is provided but `children` is not. This helps users on screen readers navigate our content.');
  var element = props.to ? 'a' : 'button';
  var elementProps = element === 'a' ? getAnchorProps(props) : getButtonProps(props);
  return React.createElement(InputRowContext.Consumer, null, function (_ref4) {
    var _classNames3;

    var isWithinInputRow = _ref4.isWithinInputRow,
        isFirstInputRowChild = _ref4.isFirstInputRowChild,
        isLastInputRowChild = _ref4.isLastInputRowChild;
    return React.createElement(element, merge({}, {
      disabled: props.isLoading || props.isDisabled,
      className: classNames((_classNames3 = {}, _defineProperty(_classNames3, styles.themedButton, true), _defineProperty(_classNames3, styles.themedButtonRoundedBordersLeft, isFirstInputRowChild || !isWithinInputRow), _defineProperty(_classNames3, styles.themedButtonRoundedBordersRight, isLastInputRowChild || !isWithinInputRow), _defineProperty(_classNames3, styles.themedButtonHasNoRightBorder, isWithinInputRow && !isLastInputRowChild), _defineProperty(_classNames3, styles.themedButtonThemePrimary, props.theme === 'primary'), _defineProperty(_classNames3, styles.themedButtonThemeTertiary, props.theme === 'tertiary'), _defineProperty(_classNames3, styles.themedButtonThemeSecondary, props.theme === 'secondary'), _defineProperty(_classNames3, styles.themedButtonThemeCaution, props.theme === 'caution'), _defineProperty(_classNames3, styles.themedButtonThemeSolid, props.theme === 'solid'), _defineProperty(_classNames3, styles.themedButtonThemePopoverPrimary, props.theme === 'popover-primary'), _defineProperty(_classNames3, styles.themedButtonThemePopoverSecondary, props.theme === 'popover-secondary'), _defineProperty(_classNames3, styles.themedButtonWidthAuto, props.width === 'auto' && !isWithinInputRow), _defineProperty(_classNames3, styles.themedButtonWidthFull, props.width === 'full' || isWithinInputRow), _defineProperty(_classNames3, styles.themedButtonWidthFullBelowSmall, props.width === 'full-below-small' && !isWithinInputRow), _classNames3)),
      'aria-label': props.accessibilityLabel,
      'data-test': props.dataTest,
      ref: ref
    }, elementProps),
    /**
     * Call functions that can wrap the `children` in HTML and CSS that aligns
     * icons with text, adds a loading indicator, and adds a flex wrapper to
     * the button.
     */
    withFlexWrapper(withLoader(withIcon(props.children, props), props), props));
  });
});
Themed.propTypes = {
  /**
   * Contents displayed within the button.
   */
  children: PropTypes.node,

  /**
   * Boolean determining whether the button is disabled. When `true` it will appear visually
   * "greyed out" and not respond to interaction.
   */
  isDisabled: PropTypes.bool,

  /**
   * Boolean determining whether the button is in a loading state. When `true` the text will
   * we replaced with a loading animation and interaction will be disabled.
   */
  isLoading: PropTypes.bool,

  /**
   * Icon from [Thumbprint Icons](/icons/) to render within the button.
   */
  icon: PropTypes.node,

  /**
   * Button's on type `submit` will submit a form when used within a `form`
   * element.
   */
  type: PropTypes.oneOf(['button', 'submit']),

  /**
   * Page to navigate to when the anchor is clicked.
   */
  to: PropTypes.string,

  /**
   * Opens the URL in a new tab when clicked.
   */
  shouldOpenInNewTab: PropTypes.bool,

  /**
   * Function that will run when the button is clicked on.
   */
  onClick: PropTypes.func,

  /**
   * Function that runs when the user hovers on the button.
   */
  onMouseEnter: PropTypes.func,

  /**
   * Function that runs when the user hovers on the button. Unlike `onMouseEnter`, `onMouseOver`
   * fires each time a child element receives focus.
   */
  onMouseOver: PropTypes.func,

  /**
   * Function that runs when the button receives focus.
   */
  onFocus: PropTypes.func,

  /**
   * Function that runs when the user hovers away from the button.
   */
  onMouseLeave: PropTypes.func,

  /**
   * Function that runs when the button loses focus.
   */
  onBlur: PropTypes.func,

  /**
   * Description of the button’s content. It is required if the button has an icon and no
   * descriptive text.
   */
  accessibilityLabel: PropTypes.string,

  /**
   * Controls the button's background, text, and border color.
   */
  theme: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'caution', 'solid', 'popover-primary', 'popover-secondary']),

  /**
   * Changes the button's `line-height`, `padding`, `border-radius`, and `font-size`.
   */
  size: PropTypes.oneOf(['small', 'large']),

  /**
   * `Button` components are as wide as the content that is passed in. The `full` option will
   * expand the width to `100%` on all screens. `full-below-small` will expand the width to 100%
   * on devices smaller than [our `small` breakpoint](/tokens/#section-breakpoint).
   */
  width: PropTypes.oneOf(['auto', 'full', 'full-below-small']),

  /**
   * A selector hook into the React component for use in automated testing environments.
   */
  dataTest: PropTypes.string
};
Themed.defaultProps = {
  children: undefined,
  type: 'button',
  isDisabled: false,
  isLoading: false,
  icon: null,
  to: null,
  shouldOpenInNewTab: false,
  onClick: null,
  onMouseEnter: undefined,
  onMouseOver: undefined,
  onFocus: undefined,
  onBlur: undefined,
  onMouseLeave: undefined,
  accessibilityLabel: undefined,
  theme: 'primary',
  size: 'large',
  width: 'auto',
  dataTest: undefined
};
export default Themed;